"use client";
import Image from "next/image";
import { useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  FaMoneyBillTransfer,
  FaListUl,
  FaBoxesStacked,
  FaCubes,
  FaPiggyBank,
  FaScrewdriverWrench,
} from "react-icons/fa6";
import Swiper, { Navigation } from "swiper";

import CarouselImg02 from "@/public/images/carousel-icon-delivery.svg";

// Import Swiper
import "swiper/swiper.min.css";
import ButtonFreeTrial from "./button-free-trial";
import Highlighter, { HighlighterItem } from "./highlighter";
Swiper.use([Navigation]);

const CarouselItem = ({
  icon,
  title,
  description,
  imageSrc,
}: {
  icon?: React.ReactNode;
  title: string;
  description: string;
  imageSrc?: string;
}) => {
  return (
    <HighlighterItem className='swiper-slide h-auto group/slide'>
      <div className='relative h-full bg-slate-900 rounded-[inherit] z-20 overflow-hidden'>
        {/* Radial gradient */}
        <div
          className='absolute bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 pointer-events-none -z-10 w-1/3 aspect-square'
          aria-hidden='true'
        >
          <div className='absolute inset-0 translate-z-0 rounded-full bg-slate-800 group-[.swiper-slide-active]/slide:bg-purple-500 transition-colors duration-500 ease-in-out blur-[60px]' />
        </div>
        <div className='flex flex-col p-6 h-full'>
          {icon ? (
            <div className='mb-3 text-purple-500'>{icon}</div>
          ) : (
            imageSrc && (
              <Image className='mb-3' src={imageSrc} width={56} height={56} alt='Carousel Icon' unoptimized={true} />
            )
          )}
          <div className='grow'>
            <div className='font-bold text-lg mb-1'>{title}</div>
            <div className='text-slate-400 mb-3'>{description}</div>
          </div>
          <div className='text-right'>
            <ButtonFreeTrial />
          </div>
        </div>
      </div>
    </HighlighterItem>
  );
};

export default function TestimonialsCarousel() {
  const [swiperInitialized, setSwiperInitialized] = useState<boolean>(false);

  useEffect(() => {
    new Swiper(".stellar-carousel", {
      breakpoints: {
        320: {
          slidesPerView: 1,
        },
        640: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
        },
      },
      observer: true,
      observeParents: true,
      grabCursor: true,
      loop: false,
      centeredSlides: false,
      initialSlide: 0,
      spaceBetween: 24,
      speed: 200,
      slideToClickedSlide: true,
      navigation: {
        nextEl: ".carousel-next",
        prevEl: ".carousel-prev",
      },
    });
    setSwiperInitialized(true);
  }, []);

  return (
    <section id='functionalities'>
      <div className='max-w-6xl mx-auto px-4 sm:px-6'>
        <div className='pt-12 md:pt-20'>
          {/* Section header */}
          <div className='max-w-3xl mx-auto text-center pb-12 md:pb-20'>
            <div>
              <div className='inline-flex font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-purple-200 pb-3'>
                Único Sistema com Múltiplas Funcionalidades
              </div>
            </div>
            <h2 className='h2 bg-clip-text text-transparent bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 pb-4'>
              Nossas Funcionalidades
            </h2>
            <p className='text-lg text-slate-400'>
              Veja nossa variedade de funcionalidades e simplifique a gestão do seu estabelecimento com nossas soluções
              específicas e inovadoras.
            </p>
          </div>

          {/* Carousel built with Swiper.js */}
          <div className='relative'>
            <div className='stellar-carousel swiper-container group'>
              <Highlighter className='swiper-wrapper min-w-[300px]' refresh={swiperInitialized}>
                {/* Carousel items */}
                <CarouselItem
                  icon={<FaMoneyBillTransfer size={56} />}
                  title='Venda Avulsa Rápida no Balcão PDV'
                  description='Simplifique suas vendas do dia a dia em segundos. Sem complicação, sem espera: clicou, vendeu, pronto!'
                />
                <CarouselItem
                  icon={<FaListUl size={56} />}
                  title='Gerador de Comandas'
                  description='Crie comandas de forma rápida e descomplicada. Fácil, intuitivo e pronto para usar!'
                />
                <CarouselItem
                  imageSrc={CarouselImg02}
                  title='Delivery'
                  description='Gerencie pedidos, controle entregadores e tenha sua própria página online para receber pedidos com facilidade.'
                />
                <CarouselItem
                  icon={<FaBoxesStacked size={56} />}
                  title='Controle Inteligente de Produtos'
                  description='Cadastre, gerencie e acompanhe produtos e combos com estoque sempre atualizado e sob controle.'
                />
                <CarouselItem
                  icon={<FaCubes size={56} />}
                  title='Controle Total do Estoque'
                  description='Acompanhe facilmente todas as entradas e saídas do seu estoque, em tempo real e sem surpresas.'
                />
                <CarouselItem
                  icon={<FaPiggyBank size={56} />}
                  title='Financeiro'
                  description='Tenha controle total das suas finanças com relatórios claros e rápidos sobre receitas e despesas.'
                />
                <CarouselItem
                  icon={<FaScrewdriverWrench size={56} />}
                  title='Administrativo'
                  description='Gerencie facilmente clientes, mesas, usuários, taxas e couvert artístico, tudo em um só lugar.'
                />
              </Highlighter>
            </div>
          </div>

          {/* Arrows */}
          <div className='flex mt-8 justify-end'>
            <button className='carousel-prev relative z-20 w-12 h-12 flex items-center justify-center group'>
              <span className='sr-only'>Previous</span>
              <FaArrowLeft className='w-4 h-4 text-slate-500 group-hover:text-purple-500 transition duration-150 ease-in-out' />
            </button>
            <button className='carousel-next relative z-20 w-12 h-12 flex items-center justify-center group'>
              <span className='sr-only'>Next</span>
              <FaArrowRight className='w-4 h-4 text-slate-500 group-hover:text-purple-500 transition duration-150 ease-in-out' />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
